import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IContaBancaria, IRetornoProvisaoOpcao, IRetornoTitulo, IRetornosVisao } from '../../interfaces/IModuloBancario';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import { BarraFiltros, BuscaOpcoes, PaginacaoModulo, useFiltroLista } from './paginacao-modulo';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = nenhuma competência escolhida. */
    competencia: string;
    contas: IContaBancaria[];
    /** A geração das minutas muda o uso dos títulos. */
    versaoMinutas: number;
    /** Ler um retorno ou vincular um título muda as minutas. */
    onAlterou: () => void;
}

const INTERVALO_MS = 4000;
const ACEITOS = '.ret,.txt,.cnab,.crt,.dat,.pdf';

const SITUACAO: Record<string, { texto: string; classe: string }> = {
    ANALISANDO: { texto: 'Conferindo', classe: 'badge-light' },
    AGUARDANDO_CONTA: { texto: 'Aguardando conta', classe: 'badge-light-warning' },
    AGUARDANDO_DECISAO: { texto: 'Aguardando decisão', classe: 'badge-light-warning' },
    LENDO: { texto: 'Lendo', classe: 'badge-light-primary' },
    LIDO: { texto: 'Lido', classe: 'badge-light-success' },
    DUPLICADO: { texto: 'Já enviado', classe: 'badge-light' },
    ERRO: { texto: 'Erro', classe: 'badge-light-danger' },
};

const brl = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const usd = (valor: number) => `US$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const descreverConta = (c: IContaBancaria) => `${c.banco} / ${c.agencia ? c.agencia + ' / ' : ''}${c.conta}${c.descricao ? ' — ' + c.descricao : ''}`;
const VAZIO: IRetornosVisao = { arquivos: [], titulos: [], dispensaJustificativa: null, dispensaEm: null, declaracaoDispensa: '', totalItens: 0, pagina: 1, tamanhoPagina: 50 };
const USO_FILTRO: Record<string, string> = { EM_MINUTA: 'Em minuta', SEM_LINHA: 'Sem linha casada', NAO_LIQUIDACAO: 'Não é liquidação' };

// DEVN3-774 — retorno de cobrança da competência: CNAB lido sem custo, francesinha com custo antes, títulos, vínculo e dispensa.
export default function RetornosCobranca({ esteiraId, competencia, contas, versaoMinutas, onAlterou }: Props) {
    const [visao, setVisao] = useState<IRetornosVisao>(VAZIO);
    const [enviando, setEnviando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [contaFrancesinha, setContaFrancesinha] = useState(0);
    const [dispensa, setDispensa] = useState<{ justificativa: string; ciencia: boolean } | null>(null);
    const [vinculo, setVinculo] = useState<{ titulo: IRetornoTitulo; opcoes: IRetornoProvisaoOpcao[]; total: number } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [ano, mes] = competencia ? competencia.split('-').map(Number) : [0, 0];
    const filtro = useFiltroLista();
    const ativas = contas.filter(c => c.ativo);

    const buscar = useCallback(async (silencioso = false) => {
        if (!ano || !mes) return;
        try {
            setVisao(await ModuloBancarioService.retornos(esteiraId, ano, mes, filtro.params));
        } catch (err: any) {
            if (!silencioso) Swal.fire({ text: err?.message ?? 'Não foi possível carregar o retorno de cobrança.', icon: 'error' });
        }
    }, [esteiraId, ano, mes, filtro.params]);

    // O filtro muda a consulta, não a competência: a lista antiga fica na tela até a nova chegar.
    useEffect(() => {
        setVisao(VAZIO);
    }, [esteiraId, ano, mes]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    useEffect(() => {
        if (versaoMinutas > 0) buscar(true);
    }, [versaoMinutas, buscar]);

    const emAndamento = visao.arquivos.some(a => a.situacao === 'ANALISANDO' || a.situacao === 'LENDO');
    useEffect(() => {
        if (!emAndamento) return;
        const id = setInterval(() => buscar(true), INTERVALO_MS);
        return () => clearInterval(id);
    }, [emAndamento, buscar]);

    // Terminada a leitura, a geração refaz as minutas: a lista precisa acompanhar.
    const processavaAntes = useRef(false);
    useEffect(() => {
        if (processavaAntes.current && !emAndamento) onAlterou();
        processavaAntes.current = emAndamento;
    }, [emAndamento, onAlterou]);

    const enviar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files || files.length === 0) return;

        setEnviando(true);
        const falhas: string[] = [];
        for (const file of Array.from(files)) {
            try {
                if (file.name.toLowerCase().endsWith('.pdf') && !contaFrancesinha)
                    throw new Error('escolha a conta bancária antes de enviar a francesinha');
                const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type || 'application/octet-stream');
                await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
                await ModuloBancarioService.registrarRetorno(esteiraId, {
                    ano, mes, nomeGerado: generatedFileName, nomeOriginal: file.name, contaBancariaId: contaFrancesinha || null,
                });
            } catch (err: any) {
                falhas.push(`${file.name}: ${err?.message ?? 'falha no envio'}`);
            }
        }
        setEnviando(false);
        if (inputRef.current) inputRef.current.value = '';
        if (falhas.length > 0) Swal.fire({ title: 'Alguns arquivos não foram enviados', html: falhas.join('<br/>'), icon: 'warning' });
        buscar();
    };

    const executar = async (acao: () => Promise<unknown>, erro: string) => {
        setSalvando(true);
        try {
            await acao();
            await buscar(true);
            return true;
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? erro, icon: 'error' });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const aguardando = visao.arquivos.filter(a => a.situacao === 'AGUARDANDO_DECISAO');
    const custoProvavel = aguardando.reduce((s, a) => s + (a.custoProvavelUsd ?? 0), 0);
    const custoMaximo = aguardando.reduce((s, a) => s + (a.custoMaximoUsd ?? 0), 0);

    const decidir = (processar: boolean, ids = aguardando.map(a => a.id)) =>
        executar(() => ModuloBancarioService.decidirRetornos(esteiraId, ids, processar), 'Não foi possível registrar a decisão.');

    const confirmarDispensa = async () => {
        if (!dispensa) return;
        const ok = await executar(
            () => ModuloBancarioService.dispensarRetornos(esteiraId, { ano, mes, justificativa: dispensa.justificativa, ciencia: dispensa.ciencia }),
            'Não foi possível dispensar.');
        if (ok) setDispensa(null);
    };

    const abrirVinculo = async (titulo: IRetornoTitulo) => {
        try {
            const { itens, total } = await ModuloBancarioService.provisoesDoTitulo(esteiraId, titulo.id);
            setVinculo({ titulo, opcoes: itens, total });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível carregar as provisões.', icon: 'error' });
        }
    };

    const tituloDoVinculo = vinculo?.titulo.id;
    const buscarProvisoes = useCallback(async (busca: string) => {
        if (!tituloDoVinculo) return;
        try {
            const { itens, total } = await ModuloBancarioService.provisoesDoTitulo(esteiraId, tituloDoVinculo, busca);
            setVinculo(v => (v ? { ...v, opcoes: itens, total } : v));
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível buscar as provisões.', icon: 'error' });
        }
    }, [esteiraId, tituloDoVinculo]);

    const vincular = async (lancamentoId: number | null) => {
        if (!vinculo) return;
        const ok = await executar(() => ModuloBancarioService.vincularTitulo(esteiraId, vinculo.titulo.id, lancamentoId), 'Não foi possível vincular.');
        if (ok) {
            setVinculo(null);
            onAlterou();
        }
    };

    const nomeArquivo = (id: number) => visao.arquivos.find(a => a.id === id)?.nomeOriginal ?? '';
    const decidida = (t: IRetornoTitulo) => !!t.minutaSituacao && t.minutaSituacao !== 'PENDENTE' && t.minutaSituacao !== 'GERADA';
    const usoDoTitulo = (t: IRetornoTitulo) => {
        if (!t.liquidacao) return 'Não é liquidação';
        if (!t.minutaId) return 'Sem linha casada';
        return t.minutaSituacao === 'PENDENTE' ? 'Em minuta pendente' : 'Em minuta';
    };

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Retorno de cobrança
                    {(enviando || emAndamento) && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                {competencia && (
                    <div className='d-flex align-items-center gap-2'>
                        {!visao.dispensaEm && (
                            <button className='btn btn-sm btn-light' onClick={() => setDispensa({ justificativa: '', ciencia: false })}>Dispensar</button>
                        )}
                        <select className='form-select form-select-sm w-auto' value={contaFrancesinha} onChange={e => setContaFrancesinha(Number(e.target.value))}>
                            <option value={0}>Identificar a conta pelo arquivo (só CNAB)</option>
                            {ativas.map(c => <option key={c.id} value={c.id}>{descreverConta(c)}</option>)}
                        </select>
                        <input ref={inputRef} type='file' multiple accept={ACEITOS} className='d-none' onChange={enviar} />
                        <button className='btn btn-sm btn-light-primary' disabled={enviando} onClick={() => inputRef.current?.click()}>
                            <i className='fas fa-upload'></i> Enviar retorno
                        </button>
                    </div>
                )}
            </div>
            <div className='card-body pt-0'>
                {!competencia ? (
                    <div className='text-muted'>Escolha a competência no topo da página.</div>
                ) : (
                    <>
                        <div className='text-muted fs-7 mb-3'>
                            CNAB 240 ou 400, ou francesinha em PDF. A liquidação vira minuta com juros, multa, desconto e tarifa quando fecha com o crédito do extrato. Sem linha no extrato, não gera minuta.
                        </div>

                        {visao.dispensaEm && (
                            <div className='notice bg-light rounded p-3 mb-3 fs-7'>
                                Retorno dispensado em {moment.utc(visao.dispensaEm).local().format('DD/MM/YYYY')}: {visao.dispensaJustificativa}
                            </div>
                        )}

                        {aguardando.length > 0 && (
                            <div className='d-flex flex-wrap align-items-center gap-3 border rounded p-3 mb-3'>
                                <span className='fs-7 flex-grow-1'>
                                    {aguardando.length} francesinha(s) aguardando decisão. Custo provável de <strong>{usd(custoProvavel)}</strong>, no máximo {usd(custoMaximo)}.
                                </span>
                                <button className='btn btn-sm btn-light' disabled={salvando} onClick={() => decidir(false)}>Descartar</button>
                                <button className='btn btn-sm btn-primary' disabled={salvando} onClick={() => decidir(true)}>Processar</button>
                            </div>
                        )}

                        {visao.arquivos.filter(a => a.situacao !== 'LIDO').map(a => (
                            <div key={a.id} className='d-flex flex-wrap align-items-center gap-2 fs-7 mb-2'>
                                <span className={`badge ${SITUACAO[a.situacao]?.classe ?? 'badge-light'}`}>{SITUACAO[a.situacao]?.texto ?? a.situacao}</span>
                                <span>{a.nomeOriginal}</span>
                                {a.mensagem && <span className='text-muted'>{a.mensagem}</span>}
                                {a.situacao === 'AGUARDANDO_CONTA' && (
                                    <select className='form-select form-select-sm w-auto' value={0} disabled={salvando}
                                        onChange={e => executar(() => ModuloBancarioService.escolherContaRetorno(esteiraId, a.id, Number(e.target.value)), 'Não foi possível escolher a conta.')}>
                                        <option value={0}>Escolha a conta</option>
                                        {ativas.map(c => <option key={c.id} value={c.id}>{descreverConta(c)}</option>)}
                                    </select>
                                )}
                                {(a.situacao === 'ERRO' || a.situacao === 'DUPLICADO' || a.situacao === 'AGUARDANDO_CONTA') && (
                                    <button className='btn btn-sm btn-link p-0 ms-auto' disabled={salvando} onClick={() => decidir(false, [a.id])}>Tirar da lista</button>
                                )}
                            </div>
                        ))}

                        {(visao.totalItens > 0 || filtro.ativo) && (
                            <BarraFiltros filtro={filtro} placeholder='Buscar por título, sacado ou arquivo' situacoes={USO_FILTRO} />
                        )}
                        {visao.titulos.length === 0 && filtro.ativo && <div className='text-muted py-4'>Nenhum título com estes filtros.</div>}

                        {visao.titulos.length > 0 && (
                            <div className='table-responsive'>
                                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mt-3'>
                                    <thead>
                                        <tr className='fw-bold text-muted'>
                                            <th>Crédito</th>
                                            <th>Título</th>
                                            <th>Sacado</th>
                                            <th className='text-end'>Nominal</th>
                                            <th className='text-end'>Acréscimos</th>
                                            <th className='text-end'>Deduções</th>
                                            <th className='text-end'>Líquido</th>
                                            <th>Uso</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visao.titulos.map(t => (
                                            <tr key={t.id}>
                                                <td className='fs-7 text-nowrap'>{t.dataCredito ? moment(t.dataCredito).format('DD/MM/YYYY') : '—'}</td>
                                                <td className='fs-7'>
                                                    {t.seuNumero ?? t.nossoNumero ?? '—'}
                                                    {t.seuNumero && t.nossoNumero && <div className='text-muted fs-8'>{t.nossoNumero}</div>}
                                                </td>
                                                <td className='fs-7'>
                                                    {t.sacadoNome ?? '—'}
                                                    {t.documentoMascarado && <div className='text-muted fs-8'>{t.documentoMascarado}</div>}
                                                </td>
                                                <td className='text-end text-nowrap fs-7'>{brl(t.valorNominal)}</td>
                                                <td className='text-end text-nowrap fs-7' title={`Juros ${brl(t.juros)} · Multa ${brl(t.multa)}`}>{brl(t.juros + t.multa)}</td>
                                                <td className='text-end text-nowrap fs-7' title={`Desconto ${brl(t.desconto)} · Abatimento ${brl(t.abatimento)} · Tarifa ${brl(t.tarifa)}`}>{brl(t.desconto + t.abatimento + t.tarifa)}</td>
                                                <td className='text-end text-nowrap fs-7'>{brl(t.valorLiquido)}</td>
                                                <td className='fs-7'>
                                                    {usoDoTitulo(t)}
                                                    {t.liquidacao && t.principal < t.valorNominal && <div className='text-muted fs-8'>Baixa de {brl(t.principal)}</div>}
                                                    {t.lancamentoEscolhidoId && <div className='text-muted fs-8'>Provisão indicada</div>}
                                                </td>
                                                <td className='text-end text-nowrap'>
                                                    {t.liquidacao && !decidida(t) && (
                                                        <button className='btn btn-sm btn-light me-1' disabled={salvando} onClick={() => abrirVinculo(t)}>Vincular</button>
                                                    )}
                                                    <button className='btn btn-sm btn-icon btn-light' title={`Abrir ${nomeArquivo(t.arquivoId)}`}
                                                        onClick={() => ModuloBancarioService.baixarRetorno(esteiraId, t.arquivoId, nomeArquivo(t.arquivoId))
                                                            .catch((err: any) => Swal.fire({ text: err?.message ?? 'Não foi possível abrir o arquivo.', icon: 'error' }))}>
                                                        <i className='fas fa-file-download'></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <PaginacaoModulo total={visao.totalItens} pagina={filtro.pagina} tamanhoPagina={filtro.tamanhoPagina} onPagina={filtro.setPagina} onTamanho={filtro.setTamanho} />
                    </>
                )}
            </div>

            {vinculo && (
                <Modal show onHide={() => setVinculo(null)} centered size='lg'>
                    <Modal.Header closeButton>
                        <Modal.Title>Provisão do título {vinculo.titulo.seuNumero ?? vinculo.titulo.nossoNumero}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='text-muted fs-7 mb-3'>O título baixa {brl(vinculo.titulo.principal)}. As minutas da competência são geradas de novo.</div>
                        <BuscaOpcoes placeholder='Buscar por histórico ou conta' mostradas={vinculo.opcoes.length} total={vinculo.total} onBuscar={buscarProvisoes} />
                        {vinculo.opcoes.length === 0 ? (
                            <div className='text-muted'>Nenhuma provisão pendente de cliente com residual suficiente nesta busca.</div>
                        ) : (
                            <table className='table table-row-dashed align-middle gs-0 gy-2'>
                                <thead>
                                    <tr className='fw-bold text-muted'><th>Data</th><th>Conta</th><th>Histórico</th><th className='text-end'>Residual</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {vinculo.opcoes.map(p => (
                                        <tr key={p.id}>
                                            <td className='fs-7 text-nowrap'>{moment(p.data).format('DD/MM/YYYY')}</td>
                                            <td className='fs-7'>{p.conta}{p.contaDescricao && <div className='text-muted fs-8'>{p.contaDescricao}</div>}</td>
                                            <td className='fs-7'>{p.historico}</td>
                                            <td className='text-end text-nowrap fs-7'>{brl(p.residual)}</td>
                                            <td className='text-end'>
                                                <button className='btn btn-sm btn-light-primary' disabled={salvando} onClick={() => vincular(p.id)}>Escolher</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {vinculo.titulo.lancamentoEscolhidoId && (
                            <button className='btn btn-light' disabled={salvando} onClick={() => vincular(null)}>Voltar à busca automática</button>
                        )}
                        <button className='btn btn-light' onClick={() => setVinculo(null)}>Fechar</button>
                    </Modal.Footer>
                </Modal>
            )}

            {dispensa && (
                <Modal show onHide={() => setDispensa(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Dispensar retorno de cobrança</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='text-muted fs-7 mb-3'>Vale para toda a empresa nesta competência. As minutas e o fechamento não mudam.</div>
                        <label className='form-label'>Justificativa</label>
                        <textarea className='form-control mb-3' rows={3} maxLength={500} value={dispensa.justificativa}
                            onChange={e => setDispensa({ ...dispensa, justificativa: e.target.value })}></textarea>
                        <label className='form-check form-check-custom'>
                            <input type='checkbox' className='form-check-input' checked={dispensa.ciencia} onChange={e => setDispensa({ ...dispensa, ciencia: e.target.checked })} />
                            <span className='form-check-label fs-7'>{visao.declaracaoDispensa}</span>
                        </label>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setDispensa(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={salvando || !dispensa.ciencia || !dispensa.justificativa.trim()} onClick={confirmarDispensa}>Dispensar</button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}
